import React, { useEffect, useState, useMemo, useRef } from "react";
import CardBasic from "../components/CardBasic";
import Table from "../components/Table";
import moment from "moment";
import {
  ExportAuthTableExcel,
  formatToRupiahValue,
  generateArrayPages,
} from "../utils/Utils";
import { ReactComponent as ArrowIcon } from "../assets/icons/arrow.svg";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { requestAuthGet } from "../api/authInstance";
import { useAuth } from "../components/auth/authContext";
import useAPI from "../hooks/useAPI";
import { useTranslation } from "react-i18next";
import Button from "../components/Button/Button";
import InputSelect from "../components/Input/InputSelect";
import withAuth from "../components/withAuth";
import Container from "../components/Container/Container";

const EwalletFragment = ({ allowed_permissions }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [date, setDate] = useState({
    startDateLabel: new Date().setDate(new Date().getDate() - 6),
    endDateLabel: new Date(),
    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [showModalLoading, setShowModalLoading] = useState(false);

  const [pageSelected, setPageSelected] = useState(1);
  const [filter, setFilter] = useState({
    retailer: userData?.outlet?.retailer?.id.toString(),
    project: null,
  });
  const [projectType, setProjectType] = useState("");

  // Balance
  const [balanceData, setBalanceData] = useState({});
  const [loadingBalance, setLoadingBalance] = useState(true);

  // EwalletTransactions
  const [ewalletTransactions, setEwalletTransactions] = useState({});
  const [loadingEwalletTransactions, setLoadingEwalletTransactions] =
    useState(true);

  const [dataBank, loadingBank, fetchBank] = useAPI("bank-account", {
    ...filter,
  });
  const [dataProject, loadingProject] = useAPI("option/project", {
    retailer: userData?.outlet?.retailer?.id.toString(),
    type: "42f7e8fe-b5de-4843-8506-707e8dc37e7c",
  });

  const [isRequire, setIsRequire] = useState({
    is_valid_bank_data: true,
    is_bank_data_available: !_.isEmpty(dataBank),
  });

  const options = useMemo(
    () => ({
      project: dataProject
        ? [
            { label: "ALL PROJECT", value: null },
            ...dataProject.map((value) => ({
              label: value.alias ?? value.name,
              value: value.id,
              type: value.type,
            })),
          ]
        : [{ label: "LOAD DATA...", value: null }],
    }),
    [dataProject],
  );

  useEffect(() => {
    GetBalance();
  }, [filter]);

  // prev
  const prevFilter = useRef(filter);
  const prevDate = useRef(date);

  useEffect(() => {
    const isRefresh =
      filter !== prevFilter.current || date !== prevDate.current;
    GetEwalletTransactions({ isRefresh });

    // Update the previous values
    prevFilter.current = filter;
    prevDate.current = date;
  }, [filter, date, pageSelected]);

  const hasDCM = useMemo(
    () =>
      userData.module.some(
        (item) => item.moduleName === "Direct Cash Management",
      ),
    [userData],
  );

  const GetBalance = async () => {
    try {
      setLoadingBalance(true);
      const { data } = await requestAuthGet("ewallet/balance", {
        params: { ...filter },
      });
      setBalanceData({ ...data.data });
    } catch (error) {
      console.error("Error fetching balance data:", error);
    } finally {
      setLoadingBalance(false);
    }
  };

  const GetEwalletTransactions = async ({ isRefresh, isDownload }) => {
    try {
      setLoadingEwalletTransactions(true);
      const { data } = await requestAuthGet("transaction", {
        params: {
          ...filter,
          ...date,
          page: isRefresh ? 1 : pageSelected,
          limit: isDownload ? 999999 : 10,
        },
      });
      if (isDownload) {
        return data.data;
      } else {
        setEwalletTransactions({ ...data });
      }
    } catch (error) {
      console.error("Error ewallet transactions data:", error);
    } finally {
      setLoadingEwalletTransactions(false);
    }
  };

  const columns = {
    tableTransactions: useMemo(
      () => [
        {
          Header: t("ewallet.date"),
          accessor: "createdAt",
          Cell: ({ value }) => (
            <div className="text-center">
              {moment.utc(value).local().format("DD-MM-YYYY HH:mm")}
            </div>
          ),
        },
        {
          Header: t("ewallet.type"),
          accessor: "transactionType",
          Cell: ({ value }) => <div className="text-center">{value}</div>,
        },
        {
          Header: "Nominal",
          accessor: "nominal",
          Cell: ({ value }) => (
            <div className="text-center">
              {parseInt(value != null ? value : 0).toLocaleString("id-ID", {
                style: "currency",
                currency: "IDR",
              })}
            </div>
          ),
        },
        {
          Header: "Status",
          accessor: "status",
          Cell: ({ value }) => (
            <div className="text-center">
              <div
                className={`inline-flex text-xs font-medium ${
                  value === "success" || value === "ACCEPT"
                    ? "bg-green-100 text-green-600"
                    : value === "reject" ||
                        value === "failed" ||
                        value === "REJECT"
                      ? "bg-red-100 text-red-600"
                      : value === "pending" || value === "Pending"
                        ? "bg-yellow-100 text-yellow-600"
                        : ""
                } rounded-full px-2.5 py-1 text-center`}
              >
                {value ?? "-"}
              </div>
            </div>
          ),
        },
        {
          Header: "Project",
          accessor: "project.name",
          Cell: ({ value }) => (
            <div className="text-center">{value ?? "-"}</div>
          ),
        },
        {
          Header: "Voucher",
          accessor: "uvc.uvc",
          Cell: ({ value }) => (
            <div className="text-center">{value ?? "-"}</div>
          ),
        },
        {
          Header: "SKU",
          accessor: "sku.name",
          Cell: ({ value }) => (
            <div className="text-center">{value ?? "-"}</div>
          ),
        },
        {
          Header: t("ewallet.description"),
          accessor: "description",
          Cell: ({ value }) => (
            <div className="text-center">{value ?? "-"}</div>
          ),
        },
      ],
      [],
    ),
  };

  const handleChange = (event) => {
    setProjectType(event.type);
    setFilter({
      ...filter,
      project: event.value,
    });
  };
  return (
    <Container>
      <div className="space-y-4 pb-10">
        {!isRequire.is_valid_bank_data && (
          <div
            className={`flex items-start rounded bg-yellow-100 px-3 py-2 text-yellow-600`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="mr-2 h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
            <span className="text-sm">
              Please update your account bank data.{" "}
              <a href="/profile" className="text-blue-500 underline">
                Click Here
              </a>
            </span>
          </div>
        )}
        <InputSelect
          // className="col-span-full sm:col-span-2"
          placeholder={t("ewallet.select")}
          options={options.project}
          onChange={handleChange}
        />
        <div className="flex flex-col space-y-4 lg:flex-row lg:space-x-2 lg:space-y-0">
          <CardBasic
            title="E-Wallet"
            balanceCard
            information={
              <div className="flex items-start space-x-2 p-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6 text-[#7D7C7C]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                  />
                </svg>
                <div className="w-full text-[11px] text-gray-500">
                  {t("ewallet.ewallet")}
                  <span className="ml-1 text-[#ED64BD]">
                    {t("ewallet.ewalletExt")}
                  </span>
                </div>
              </div>
            }
          >
            <>
              <div className="mt-4">
                <div className="flex flex-col gap-3">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("ewallet.ewalletBalance")}
                    </p>
                    <h5 className="text-2xl font-bold">
                      {loadingBalance ? (
                        <div className="h-6 animate-pulse rounded bg-gray-200"></div>
                      ) : (
                        formatToRupiahValue(balanceData?.ewalletBalance)
                      )}
                    </h5>
                  </div>
                  {allowed_permissions?.includes("Add") && (
                    <Button
                      label="Tarik Dana"
                      variant="gradient"
                      startIcon={<ArrowIcon className="rotate-90" />}
                      onClick={() => {
                        navigate("/withdraw", {
                          state: {
                            detailVoucher: {},
                            saldo: {
                              ewalletBalance: balanceData?.ewalletBalance,
                              transitBalance: balanceData?.transitBalance,
                              depositBalance: balanceData?.depositBalance,
                            },
                            bankAccount: _.first(dataBank),
                          },
                        });
                      }}
                      disabled={!isRequire.is_valid_bank_data}
                    />
                  )}
                  <Button
                    label="Lihat Mutasi E-wallet"
                    variant="default"
                    onClick={() => {
                      navigate("/transaction");
                    }}
                  />
                </div>
              </div>
            </>
          </CardBasic>
          <CardBasic
            title="Deposit"
            balanceCard
            information={
              <div className="flex items-start space-x-2 p-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6 text-[#7D7C7C]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                  />
                </svg>
                <div className="w-full text-[11px] text-gray-500">
                  {t("ewallet.deposit")}
                </div>
              </div>
            }
          >
            <div className="flex flex-col gap-3">
              <div className="mt-4">
                <p className="text-sm text-gray-500">
                  {t("ewallet.depositBalance")}
                </p>
                <h5 className="text-2xl font-bold">
                  {loadingBalance ? (
                    <div className="h-6 animate-pulse rounded bg-gray-200"></div>
                  ) : (
                    formatToRupiahValue(balanceData?.depositBalance)
                  )}
                </h5>
              </div>
              {hasDCM &&
                projectType === "6f04a3c4-76bb-4a2a-89e4-57fea266fb16" && (
                  <>
                    <Button
                      label="Ajukan Proposal Taktis"
                      variant="gradient-secondary"
                      fullWidth
                      onClick={() => {
                        navigate("/direct-cash-management", {
                          state: { mode: "create" },
                        });
                      }}
                    />
                    <Button
                      label="Riwayat Pengajuan"
                      variant="default"
                      fullWidth
                      onClick={() => {
                        navigate("/direct-cash-management");
                      }}
                    />
                  </>
                )}
            </div>
          </CardBasic>
          {/* {hasDCM && projectType === "6f04a3c4-76bb-4a2a-89e4-57fea266fb16" && (
            <div className="mb-2 mt-5">
              <Button
                label="Riwayat Pengajuan"
                variant="default"
                fullWidth
                onClick={() => {
                  navigate("/direct-cash-management");
                }}
              />
            </div>
          )} */}
          {/* {userData?.isTransit &&  */}
          <CardBasic
            title="Transit"
            balanceCard
            information={
              <div className="flex items-start space-x-2 p-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6 text-[#7D7C7C]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                  />
                </svg>
                <div className="w-full text-[11px] text-gray-500">
                  {t("ewallet.transit")}
                </div>
              </div>
            }
          >
            <>
              <div className="mt-4">
                <p className="text-sm text-gray-500">
                  {t("ewallet.transitBalance")}
                </p>
                <h5 className="text-2xl font-bold">
                  {loadingBalance ? (
                    <div className="h-6 animate-pulse rounded bg-gray-200"></div>
                  ) : (
                    formatToRupiahValue(balanceData?.transitBalance)
                  )}
                </h5>
              </div>
            </>
          </CardBasic>
          {/* } */}
        </div>
      </div>
    </Container>
  );
};

export default withAuth(EwalletFragment);
